"use client";

import { useRef, useState } from "react";
import { ChevronLeft, ChevronRight } from "lucide-react";
import { cn } from "@/lib/utils/cn";
import {
  useFloating,
  useClick,
  useDismiss,
  useInteractions,
  offset,
  flip,
  arrow,
  FloatingPortal,
  FloatingArrow,
} from "@floating-ui/react";

export interface YearPickerProps {
  value?: string | number;
  onChange: (year: string) => void;
  minYear?: number;
  maxYear?: number;
  /** Horizontal alignment of the trigger value text. Defaults to "left". */
  textAlign?: "left" | "center" | "right";
}

const textAlignClass = {
  left: "text-left",
  center: "text-center",
  right: "text-right",
} as const;

export function YearPicker({
  value,
  onChange,
  minYear = 1800,
  maxYear = new Date().getFullYear(),
  textAlign = "left",
}: YearPickerProps) {
  const [isOpen, setIsOpen] = useState(false);
  const [startYear, setStartYear] = useState(() => {
    const selected = value ? parseInt(String(value)) : maxYear;
    return Math.max(minYear, Math.floor(selected / 10) * 10);
  });
  const arrowRef = useRef(null);

  const { refs, floatingStyles, context } = useFloating({
    open: isOpen,
    onOpenChange: setIsOpen,
    middleware: [offset(8), flip(), arrow({ element: arrowRef })],
    placement: "bottom-start",
  });

  const click = useClick(context);
  const dismiss = useDismiss(context);
  const { getReferenceProps, getFloatingProps } = useInteractions([
    click,
    dismiss,
  ]);

  const referenceProps = getReferenceProps();
  const displayValue = value ? String(value) : "—";
  const years: number[] = [];

  for (let i = startYear; i < startYear + 25; i++) {
    years.push(i);
  }

  const handlePrev = () => {
    setStartYear((prev) => Math.max(minYear, prev - 10));
  };

  const handleNext = () => {
    setStartYear((prev) => {
      const nextStart = prev + 10;
      return nextStart + 25 <= maxYear + 10 ? nextStart : prev;
    });
  };

  const handleSelectYear = (year: number) => {
    onChange(String(year));
    setIsOpen(false);
  };

  return (
    <>
      {/* Trigger button */}
      <button
        ref={refs.setReference}
        type="button"
        {...referenceProps}
        onClick={(e) => {
          e.stopPropagation();
          (referenceProps as React.HTMLAttributes<HTMLButtonElement>).onClick?.(e);
        }}
        className={cn(
          "h-7 text-xs font-medium text-gray-900 bg-gray-50 border rounded-lg px-2 focus:outline-none focus:ring-1 w-full transition-colors",
          textAlignClass[textAlign],
          "border-gray-200 focus:ring-blue-600 focus:border-blue-600",
        )}
      >
        {displayValue}
      </button>

      {/* Floating popover */}
      {isOpen && (
        <FloatingPortal>
          <div
            ref={refs.setFloating}
            style={floatingStyles}
            {...getFloatingProps()}
            className="z-50 bg-white border border-gray-200 rounded-lg shadow-lg w-max p-3"
          >
            <FloatingArrow
              ref={arrowRef}
              context={context}
              fill="white"
              strokeWidth={1}
              stroke="#e5e7eb"
              width={12}
              height={6}
            />
            {/* Header with navigation */}
            <div className="flex items-center justify-between gap-2 mb-2">
              <button
                type="button"
                onClick={handlePrev}
                disabled={startYear === minYear}
                className="p-1 hover:bg-gray-100 rounded disabled:opacity-50 disabled:cursor-not-allowed"
                aria-label="Previous decade"
              >
                <ChevronLeft className="w-4 h-4" />
              </button>
              <span className="text-xs font-semibold text-gray-700 min-w-16 text-center">
                {startYear} – {startYear + 24}
              </span>
              <button
                type="button"
                onClick={handleNext}
                disabled={startYear + 25 > maxYear}
                className="p-1 hover:bg-gray-100 rounded disabled:opacity-50 disabled:cursor-not-allowed"
                aria-label="Next decade"
              >
                <ChevronRight className="w-4 h-4" />
              </button>
            </div>

            {/* Year grid (5 columns) */}
            <div className="grid grid-cols-5 gap-1">
              {years.map((year) => {
                const isOutOfRange = year > maxYear || year < minYear;
                return (
                  <button
                    key={year}
                    type="button"
                    disabled={isOutOfRange}
                    onClick={() => !isOutOfRange && handleSelectYear(year)}
                    className={cn(
                      "w-10 h-7 text-xs rounded border transition-colors",
                      isOutOfRange
                        ? "bg-gray-50 border-gray-100 text-gray-300 cursor-not-allowed"
                        : String(value) === String(year)
                          ? "bg-blue-600 text-white border-blue-600 font-semibold"
                          : "bg-white border-gray-200 text-gray-700 hover:bg-blue-50 hover:border-blue-300",
                    )}
                  >
                    {year}
                  </button>
                );
              })}
            </div>
          </div>
        </FloatingPortal>
      )}
    </>
  );
}
